import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import EnergyAdminApiService from '../../../api/EnergyAdminApiService';
import EnergyApiService from '../../../api/EnergyApiService';
import LocationApiService from '../../../api/LocationApiService';
import EditMenuButton from '../../Modal/EditMenuButton';
import ConfirmDeleteDialogHazard from '../../Modal/ConfirmDeleteHazard';
import EditEnergySiteModal from '../../Modal/Admin/EditEnergySiteModal';
import EditEnergyMeterModal from '../../Modal/Admin/EditEnergyMeterModal';
import { useSnackbar } from '../../Common/SnackbarProvider';

const energyAdminApiService = new EnergyAdminApiService();
const energyApiService = new EnergyApiService();
const locationApiService = new LocationApiService();

/**
 * Energy sites and the meters on them. Everything else in the module hangs off these two tables:
 * readings need a meter, weather needs a site's coordinates, and both need to exist before anyone
 * can enter anything on the Site Data page.
 */
export default function EnergySetup () {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [locations, setLocations] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [sites, setSites] = useState([]);
    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [meters, setMeters] = useState([]);
    const [utilities, setUtilities] = useState([]);
    const [units, setUnits] = useState([]);
    const [loadingSites, setLoadingSites] = useState(false);
    const [loadingMeters, setLoadingMeters] = useState(false);

    const [siteModalOpen, setSiteModalOpen] = useState(false);
    const [meterModalOpen, setMeterModalOpen] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [editingMeter, setEditingMeter] = useState(null);
    const [deletingSite, setDeletingSite] = useState(null);
    const [deletingMeter, setDeletingMeter] = useState(null);

    const selectedSite = useMemo(() => sites.find(site => site.id === selectedSiteId) ?? null, [sites, selectedSiteId]);

    useEffect(() => {
        async function loadReferenceData () {
            const [locationResponse, utilityResponse, unitResponse] = await Promise.all([
                locationApiService.getFullLocationsForCurrentOrg(),
                energyAdminApiService.getUtilities(),
                energyApiService.getUnits(),
            ]);

            const projects = (locationResponse?.data ?? []).filter(project => project.pjActive > 0);
            setLocations(projects);
            if (projects.length > 0) {
                setProjectId(current => current || projects[0].pjId);
            }

            if (utilityResponse?.success) setUtilities(utilityResponse.data);
            if (unitResponse?.success) setUnits(unitResponse.data);
        }

        loadReferenceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSites = useCallback(async () => {
        if (!projectId) return;

        setLoadingSites(true);
        try {
            const response = await energyAdminApiService.getSites(projectId);
            if (!response?.success) {
                snackbar.error(response?.message ?? t('energySetup.toast.loadFailed'));
                setSites([]);
                return;
            }

            setSites(response.data);
            setSelectedSiteId(current => (response.data.some(site => site.id === current)
                ? current
                : response.data[0]?.id ?? null));
        } finally {
            setLoadingSites(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => { loadSites(); }, [loadSites]);

    const loadMeters = useCallback(async () => {
        if (!selectedSiteId) {
            setMeters([]);
            return;
        }

        setLoadingMeters(true);
        try {
            const response = await energyAdminApiService.getMeters(selectedSiteId);
            setMeters(response?.success ? response.data : []);
        } finally {
            setLoadingMeters(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSiteId]);

    useEffect(() => { loadMeters(); }, [loadMeters]);

    const siteColumns = useMemo(() => [
        { field: 'code', headerName: t('energySetup.field.code'), flex: 0.7 },
        { field: 'name', headerName: t('energySetup.field.name'), flex: 1.4 },
        {
            field: 'city',
            headerName: t('energySetup.column.place'),
            flex: 1.2,
            valueGetter: params => [params.row.city, params.row.stateProvince].filter(Boolean).join(', '),
        },
        {
            field: 'canFetchWeather',
            headerName: t('energySetup.column.weather'),
            flex: 0.9,
            renderCell: params => (params.row.canFetchWeather
                ? <Chip size="small" color="success" variant="outlined" label={t('energySetup.weather.ready')} />
                : <Chip size="small" color="warning" variant="outlined" label={t('energySetup.weather.missing')} />),
        },
        { field: 'meterCount', headerName: t('energySetup.column.meters'), flex: 0.6, align: 'right', headerAlign: 'right' },
        {
            field: 'isActive',
            headerName: t('common.active'),
            flex: 0.5,
            renderCell: params => (params.row.isActive ? t('common.yShort') : t('common.nShort')),
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.4,
            filterable: false,
            sortable: false,
            renderCell: params => (
                <EditMenuButton
                    params={params}
                    /* The menu sets the record first, then flips the dialog; the dialog here opens
                       off the record itself, so the flag setter has nothing left to do. */
                    setCurrentSelectedRecord={setDeletingSite}
                    setShowDeleteDialog={() => {}}
                    onEditFunc={() => {
                        setEditingSite(params.row);
                        setSiteModalOpen(true);
                    }}
                />
            ),
        },
    ], [t]);

    const meterColumns = useMemo(() => [
        {
            field: 'commodityName',
            headerName: t('energySetup.field.commodity'),
            flex: 0.8,
            valueGetter: params => t(`siteData.commodity.${params.row.commodityName}`),
        },
        { field: 'utilityName', headerName: t('energySetup.field.utility'), flex: 1 },
        { field: 'accountNumber', headerName: t('energySetup.field.accountNumber'), flex: 1 },
        { field: 'meterNumber', headerName: t('energySetup.field.meterNumber'), flex: 0.8 },
        { field: 'defaultUnitCode', headerName: t('energySetup.field.defaultUnit'), flex: 0.5 },
        {
            field: 'resolutionName',
            headerName: t('energySetup.field.resolution'),
            flex: 0.7,
            valueGetter: params => t(`energySetup.resolution.${params.row.resolutionName.charAt(0).toLowerCase()}${params.row.resolutionName.slice(1)}`),
        },
        { field: 'billCount', headerName: t('energySetup.column.readings'), flex: 0.6, align: 'right', headerAlign: 'right' },
        {
            field: 'isActive',
            headerName: t('common.active'),
            flex: 0.5,
            renderCell: params => (params.row.isActive ? t('common.yShort') : t('common.nShort')),
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.4,
            filterable: false,
            sortable: false,
            renderCell: params => (
                <EditMenuButton
                    params={params}
                    setCurrentSelectedRecord={setDeletingMeter}
                    setShowDeleteDialog={() => {}}
                    onEditFunc={() => {
                        setEditingMeter(params.row);
                        setMeterModalOpen(true);
                    }}
                />
            ),
        },
    ], [t]);

    const onDeleteSite = async (confirmed) => {
        if (confirmed && deletingSite) {
            const response = await energyAdminApiService.deleteSite(deletingSite.id);
            if (response?.success) {
                snackbar.success(t('energySetup.toast.siteDeleted', { code: deletingSite.code }));
                await loadSites();
            } else {
                snackbar.error(response?.message ?? t('energySetup.toast.deleteFailed'));
            }
        }

        setDeletingSite(null);
    };

    const onDeleteMeter = async (confirmed) => {
        if (confirmed && deletingMeter) {
            const response = await energyAdminApiService.deleteMeter(deletingMeter.id);
            if (response?.success) {
                snackbar.success(t('energySetup.toast.meterDeleted', { account: deletingMeter.accountNumber }));
                await loadMeters();
            } else {
                snackbar.error(response?.message ?? t('energySetup.toast.deleteFailed'));
            }
        }

        setDeletingMeter(null);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
            <Box>
                <Typography variant="h4">{t('energySetup.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('energySetup.subtitle')}</Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="setup-location">{t('fieldName.location')}</InputLabel>
                    <Select
                        labelId="setup-location"
                        label={t('fieldName.location')}
                        value={projectId}
                        onChange={event => setProjectId(event.target.value)}
                    >
                        {locations.map(location => (
                            <MenuItem key={location.pjId} value={location.pjId}>{location.pjName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">{t('energySetup.sites')}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={!projectId}
                        onClick={() => { setEditingSite(null); setSiteModalOpen(true); }}
                    >
                        {t('energySetup.addSite')}
                    </Button>
                </Stack>
                <Box sx={{ height: 320, width: '100%' }}>
                    <DataGridPro
                        rows={sites}
                        columns={siteColumns}
                        loading={loadingSites}
                        getRowId={row => row.id}
                        selectionModel={selectedSiteId ? [selectedSiteId] : []}
                        onSelectionModelChange={model => setSelectedSiteId(model[0] ?? null)}
                        hideFooterSelectedRowCount
                    />
                </Box>
                {sites.length === 0 && !loadingSites && (
                    <Alert severity="info" sx={{ mt: 2 }}>{t('energySetup.noSites')}</Alert>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">
                        {selectedSite
                            ? t('energySetup.metersAt', { code: selectedSite.code })
                            : t('energySetup.meters')}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={!selectedSite}
                        onClick={() => { setEditingMeter(null); setMeterModalOpen(true); }}
                    >
                        {t('energySetup.addMeter')}
                    </Button>
                </Stack>
                {selectedSite
                    ? (
                        <Box sx={{ height: 320, width: '100%' }}>
                            <DataGridPro
                                rows={meters}
                                columns={meterColumns}
                                loading={loadingMeters}
                                getRowId={row => row.id}
                                hideFooterSelectedRowCount
                            />
                        </Box>
                    )
                    : <Alert severity="info">{t('energySetup.selectSite')}</Alert>}
            </Paper>

            <EditEnergySiteModal
                open={siteModalOpen}
                projectId={projectId === '' ? undefined : projectId}
                currentSite={editingSite}
                onCancel={() => { setSiteModalOpen(false); setEditingSite(null); }}
                onSave={async () => {
                    setSiteModalOpen(false);
                    setEditingSite(null);
                    await loadSites();
                }}
            />

            <EditEnergyMeterModal
                open={meterModalOpen}
                siteId={selectedSiteId ?? undefined}
                currentMeter={editingMeter}
                utilities={utilities}
                units={units}
                onCancel={() => { setMeterModalOpen(false); setEditingMeter(null); }}
                onSave={async () => {
                    setMeterModalOpen(false);
                    setEditingMeter(null);
                    await loadMeters();
                    await loadSites();
                }}
            />

            <ConfirmDeleteDialogHazard
                open={deletingSite !== null}
                onClose={onDeleteSite}
                deleteTitle={t('energySetup.confirmDeleteSiteTitle')}
                deleteLabel={t('energySetup.confirmDeleteSite', { code: deletingSite?.code ?? '' })}
            />

            <ConfirmDeleteDialogHazard
                open={deletingMeter !== null}
                onClose={onDeleteMeter}
                deleteTitle={t('energySetup.confirmDeleteMeterTitle')}
                deleteLabel={t('energySetup.confirmDeleteMeter', { account: deletingMeter?.accountNumber ?? '' })}
            />
        </Box>
    );
}
